export const pageIndex = (config) => `import React from 'react';
import { loadStripe } from '@stripe/stripe-js';

// Make sure to call \`loadStripe\` outside of a component’s render to avoid
// recreating the \`Stripe\` object on every render.
{+load-stripe}
const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
);
{/load-stripe}

{+preview-page}
export default function PreviewPage() {
{/preview-page}
  React.useEffect(() => {
    {+back}
    // Check to see if this is a redirect back from Checkout
    const query = new URLSearchParams(window.location.search);

    if (query.get('success')) {
      console.log('Order placed! You will receive an email confirmation.');
    }

    if (query.get('canceled')) {
      console.log('Order canceled -- continue to shop around and checkout when you’re ready.');
    }
    {/back}
  }, []);

  return (
    {+fetch-checkout-session}
    <form action="/api/checkout_sessions" method="POST">
    {/fetch-checkout-session}
      <section>
        {+add-button}
        <button type="submit" role="link">
          Checkout
        </button>
        {/add-button}
      </section>
      <style jsx>
        {\`
          section {
            background: #ffffff;
            display: flex;
            flex-direction: column;
            width: 400px;
            height: 112px;
            border-radius: 6px;
            justify-content: space-between;
          }
          button {
            height: 36px;
            background: #556cd6;
            border-radius: 4px;
            color: white;
            border: 0;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            box-shadow: 0px 4px 5.5px 0px rgba(0, 0, 0, 0.07);
          }
          button:hover {
            opacity: 0.8;
          }
        \`}
      </style>
    </form>
  );
}
`;

export const checkoutIndex = ({
  activePrice,
  checkoutMode,
  demo: {paymentMethods},
}) => `{+setup}
const stripe = require('stripe')(process.env.STRIPE_SECRET_KEY);
{/setup}

export default async function handler(req, res) {
  if (req.method === 'POST') {
    try {
      {+create}
      // Create Checkout Sessions from body params.
      const session = await stripe.checkout.sessions.create({
        {?customize prefill-email}
        customer_email: 'customer@example.com',
        {/prefill-email}
        {?customize submit-button}
        submit_type: 'donate',
        {/submit-button}
        {?customize address}
        billing_address_collection: 'auto',
        shipping_address_collection: {
          allowed_countries: ['US', 'CA'],
        },
        {/address}
        {+line-items}
        line_items: [
          {
            ${
              activePrice
                ? `price: '${activePrice.id}'`
                : `// TODO: replace this with the \`price\` of the product you want to sell
            price: '{{PRICE_ID}}'`
            },
            quantity: 1,
          },
        ],
        {/line-items}
        {+payment-methods}
        payment_method_types: [
          ${
            paymentMethods.length
              ? paymentMethods.map((key) => `'${key}',`).join('\n          ')
              : '// TODO: pass at least one option here'
          }
        ],
        {/payment-methods}
        {+mode}
        mode: '${checkoutMode}',
        {/mode}
        {+urls}
        success_url: \`\${req.headers.origin}/?success=true&session_id={CHECKOUT_SESSION_ID}\`,
        cancel_url: \`\${req.headers.origin}/?canceled=true\`,
        {/urls}
        {?automatic-tax automatic-tax-param}
        automatic_tax: {enabled: true},
        {/automatic-tax-param}
      });

      res.redirect(303, session.url);
      {/create}
    } catch (err) {
      res.status(err.statusCode || 500).json(err.message);
    }
  } else {
    res.setHeader('Allow', 'POST');
    res.status(405).end('Method Not Allowed');
  }
}
`;

export const dotenv = (config) => `{+set-env-vars}# Stripe keys
# https://dashboard.stripe.com/apikeys
NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=${config.keys.publishable}
STRIPE_SECRET_KEY=${config.keys.secret}
# Set this environment variable to support webhooks — https://stripe.com/docs/webhooks/signatures
# STRIPE_WEBHOOK_SECRET=whsec_12345
{/set-env-vars}`;

export const dotenvExample = `# Stripe keys
# https://dashboard.stripe.com/apikeys
NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=pk_12345
STRIPE_SECRET_KEY=sk_12345
# Set this environment variable to support webhooks — https://stripe.com/docs/webhooks/signatures
# STRIPE_WEBHOOK_SECRET=whsec_12345
`;

export const packageJson = `{
  "name": "stripe-sample",
  "version": "0.0.0",
  "description": "Full-stack example using Next.js",
  "scripts": {
    "dev": "next",
    "build": "next build",
    "start": "next start"
  },
  "dependencies": {
    "@stripe/react-stripe-js": "1.4.1",
    "@stripe/stripe-js": "1.15.1",
    "micro": "latest",
    "micro-cors": "latest",
    "next": "latest",
    "react": "^17.0.2",
    "react-dom": "^17.0.2",
    "stripe": "8.157.0"
  }
}
`;

export const gitIgnore = `
.DS_Store
.vscode

# Node files
node_modules/

# Next.js
.next
.vercel
.env
.env*.local
next-env.d.ts
`;

export const webhooksIndex = `
import Stripe from 'stripe';
import { buffer } from 'micro';
import Cors from 'micro-cors';

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY);

const webhookSecret = process.env.STRIPE_WEBHOOK_SECRET;

// Stripe requires the raw body to construct the event.
export const config = {
  api: {
    bodyParser: false,
  },
};

const cors = Cors({
  allowMethods: ['POST', 'HEAD'],
});

const webhookHandler = async (req, res) => {
  if (req.method === 'POST') {
    const buf = await buffer(req);
    const signature = req.headers['stripe-signature'];

    let event;
    try {
      event = stripe.webhooks.constructEvent(
        buf.toString(),
        signature,
        webhookSecret
      );
    } catch (err) {
      // On error, log and return the error message.
      console.log(\`❌ Error message: \${err.message}\`);
      res.status(400).send(\`Webhook Error: \${err.message}\`);
      return;
    }

    // Successfully constructed event.
    console.log('✅ Success:', event.id);

    switch (event.type) {
      case 'payment_intent.succeeded': {
        const paymentIntent = event.data.object;
        console.log(\`PaymentIntent status: \${paymentIntent.status}\`);
        break;
      }
      case 'payment_intent.payment_failed': {
        const paymentIntent = event.data.object;
        console.log(
          \`❌ Payment failed: \${paymentIntent.last_payment_error?.message}\`
        );
        break;
      }
      case 'charge.succeeded': {
        const charge = event.data.object;
        console.log(\`Charge id: \${charge.id}\`);
        break;
      }
      default: {
        console.warn(\`Unhandled event type: \${event.type}\`);
        break;
      }
    }

    // Return a response to acknowledge receipt of the event.
    res.json({ received: true });
  } else {
    res.setHeader('Allow', 'POST');
    res.status(405).end('Method Not Allowed');
  }
};

export default cors(webhookHandler);
`;
